/**
 * useAIPromptEdit Hook
 *
 * Custom hook for AI-assisted image prompt editing
 * Manages API calls, loading states, and error handling
 */

import { useCallback, useState, useTransition } from 'react';
import { useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

interface UseAIPromptEditOptions {
  promptId: string;
  originalPrompt: string;
  postId: string;
  onSuccess?: (editedPrompt: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for AI-assisted image prompt editing
 *
 * @param options - Configuration options
 * @returns AI editing state and control functions
 */
export function useAIPromptEdit({
  promptId,
  originalPrompt,
  postId,
  onSuccess,
  onError,
}: UseAIPromptEditOptions) {
  const [isPending, startTransition] = useTransition();
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [error, setError] = useState<Error | null>(null);

  // Mutations for saving edited prompt (check if this mutation exists)
  // This should create a new prompt record and update the post's currentPromptId
  const createEditedPrompt = useMutation(api.imagePrompts.createEditedPrompt);

  // Abort controller for cancelling ongoing requests
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  /**
   * Execute AI edit with instruction
   */
  const executeEdit = useCallback(
    async (instruction: string) => {
      if (!instruction.trim()) {
        const err = new Error('Instruction cannot be empty');
        setError(err);
        toast.error('Instruction required', {
          description: 'Please provide editing instructions before submitting.',
        });
        if (onError) {
          onError(err);
        }
        return;
      }

      // Show loading toast
      toast.info('AI is editing your prompt...', {
        description: 'This may take a few seconds.',
      });

      // Reset state
      setEditedPrompt('');
      setError(null);
      setIsEditing(true);

      // Create abort controller for this request
      const controller = new AbortController();
      setAbortController(controller);

      try {
        const convexSiteUrl = import.meta.env.VITE_CONVEX_SITE_URL as string;
        const response = await fetch(`${convexSiteUrl}/ai/edit-image-prompt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            promptId,
            originalContent: originalPrompt,
            instruction,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'AI prompt editing failed');
        }

        // Parse the JSON response
        const result = await response.json();

        if (!result.success || !result.editedContent) {
          throw new Error(result.error || 'AI prompt editing failed');
        }

        const finalPrompt = result.editedContent;
        setEditedPrompt(finalPrompt);
        setIsEditing(false);
        setAbortController(null);

        toast.success('AI editing completed!', {
          description: 'Review the edited prompt and save to regenerate the image.',
        });

        if (onSuccess) {
          onSuccess(finalPrompt);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Request was cancelled
          setIsEditing(false);
          setAbortController(null);
          return;
        }

        setIsEditing(false);
        setAbortController(null);
        const errorObj = err instanceof Error ? err : new Error('AI prompt editing failed');
        setError(errorObj);

        toast.error('AI prompt editing failed', {
          description: errorObj.message || 'An unexpected error occurred. Please try again.',
        });

        if (onError) {
          onError(errorObj);
        }
      }
    },
    [promptId, originalPrompt, onSuccess, onError]
  );

  /**
   * Cancel ongoing AI edit
   */
  const cancelEdit = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    setIsEditing(false);
    setEditedPrompt('');
    setError(null);
  }, [abortController]);

  /**
   * Save edited prompt to database (creates new prompt record)
   */
  const saveEdit = useCallback(
    async (prompt: string) => {
      if (!prompt.trim()) {
        const err = new Error('Cannot save empty prompt');
        setError(err);
        toast.error('Cannot save empty prompt', {
          description: 'Please generate a prompt first before saving.',
        });
        if (onError) {
          onError(err);
        }
        return false;
      }

      setIsSaving(true);
      try {
        // Save to database - creates new prompt record and updates post
        await startTransition(async () => {
          await createEditedPrompt({
            postId: postId as Id<'posts'>,
            promptText: prompt,
            editedFrom: promptId as Id<'imagePrompts'>,
          });
        });

        setIsSaving(false);
        toast.success('Prompt saved!', {
          description: 'Your edited prompt has been saved. Ready to regenerate image.',
        });
        return true;
      } catch (err) {
        setIsSaving(false);
        const errorObj = err instanceof Error ? err : new Error('Failed to save prompt');
        setError(errorObj);
        toast.error('Failed to save prompt', {
          description: errorObj.message || 'An error occurred while saving. Please try again.',
        });
        if (onError) {
          onError(errorObj);
        }
        return false;
      }
    },
    [postId, promptId, createEditedPrompt, onError]
  );

  /**
   * Reset to original prompt
   */
  const reset = useCallback(() => {
    setEditedPrompt('');
    setError(null);
    setIsEditing(false);
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
  }, [abortController]);

  return {
    // Content state
    editedPrompt,
    hasContent: editedPrompt.length > 0,

    // Loading states
    isEditing,
    isSaving: isSaving || isPending,

    // Error state
    error,

    // Actions
    executeEdit,
    cancelEdit,
    saveEdit,
    reset,
  };
}
